import {
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import type {
  BackupPolicyType,
  Condition,
  Provider,
  ResourceType,
} from '@repo/api-gw-sdk';

import { ConditionEditor } from '@/components/conditions/ConditionEditor';
import { isEmptyCondition, type FilterProperty } from '@/types/advanceFilter';

import type { ResourcesScopeType } from './resourceSelectionDynamicViewer';

import {
  DynamicResourcesAllExcluded,
  DynamicResourcesAllIncluded,
} from '../../app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/dynamicResourcesEmptyState';

export const ResourceSelectionDynamicEditor = ({
  condition,
  setCondition,
  supportedProviders,
  supportedProperties,
  supportedResourceTypes,
  conditionTitle,
  allExcludedDescription,
  allIncludedDescription,
  resourcesScopeType,
  setResourcesScopeType,
  selectedResourceTypes,
  policyType,
}: {
  condition?: Condition;
  setCondition: (condition?: Condition) => void;
  supportedProviders?: Provider[];
  supportedProperties: FilterProperty[][];
  supportedResourceTypes?: ResourceType[];
  conditionTitle: React.ReactNode;
  allExcludedDescription: string;
  allIncludedDescription: string;
  resourcesScopeType: ResourcesScopeType;
  setResourcesScopeType: (resourcesScopeType: ResourcesScopeType) => void;
  selectedResourceTypes?: ResourceType[];
  policyType?: BackupPolicyType;
}) => {
  return (
    <Stack gap='24px' width='100%' flexGrow={1} minHeight={0} overflow='auto'>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h3'>Dynamic resource selection</Typography>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          gap='8px'
        >
          <Typography variant='subtitle1'>Selection method</Typography>
          <Select
            data-testid='resource-selection-method-select'
            variant='standard'
            value={resourcesScopeType}
            onChange={(e: SelectChangeEvent) =>
              setResourcesScopeType(e.target.value as ResourcesScopeType)
            }
          >
            <MenuItem
              key='all_included'
              value='allIncluded'
              data-testid='include-all-option'
            >
              Include all
            </MenuItem>
            <MenuItem
              key='use_conditions'
              value='useConditions'
              data-testid='use-conditions-option'
            >
              Use conditions
            </MenuItem>
            <MenuItem
              key='all_excluded'
              value='allExcluded'
              data-testid='exclude-all-option'
            >
              Exclude all
            </MenuItem>
          </Select>
        </Stack>
      </Stack>
      <Divider />
      <Stack
        marginTop='16px'
        width='100%'
        flexGrow={1}
        minHeight={0}
        justifyContent={
          resourcesScopeType === 'useConditions' ? 'flex-start' : 'center'
        }
      >
        {resourcesScopeType === 'useConditions' ? (
          <Stack gap='24px'>
            {typeof conditionTitle === 'string' ? (
              <Typography variant='subtitle1'>{conditionTitle}</Typography>
            ) : (
              conditionTitle
            )}
            <ConditionEditor
              options={{
                supportGrouping: true,
                supportedProperties,
              }}
              condition={
                !condition || isEmptyCondition(condition)
                  ? undefined
                  : condition
              }
              onChange={setCondition}
              supportedProviders={supportedProviders}
              supportedResourceTypes={supportedResourceTypes}
            />
          </Stack>
        ) : resourcesScopeType === 'allExcluded' ? (
          <DynamicResourcesAllExcluded description={allExcludedDescription} />
        ) : (
          <DynamicResourcesAllIncluded
            selectedResourceTypes={selectedResourceTypes}
            policyType={policyType}
            description={allIncludedDescription}
          />
        )}
      </Stack>
    </Stack>
  );
};
